import React, { useState, useEffect } from 'react';
import { Link, CircularProgress } from '@mui/material';
import { useRevealSourcedCandidatePhoneNumberMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useToast, ToastType } from 'services/toast';
import { FailedMessageWithHref } from 'common/ui';

type SourcingRevealNumberLinkProps = {
  number?: string | null;
  fetchPhoneNumberAttempted: boolean;
  sourcedCandidateId: string;
  linkedInProfileUrl: string;
};

export const SourcingRevealNumberLink: React.FC<
  SourcingRevealNumberLinkProps
> = ({
  number,
  fetchPhoneNumberAttempted,
  sourcedCandidateId,
  linkedInProfileUrl
}) => {
  const [revealPhoneNumber, { loading }] =
    useRevealSourcedCandidatePhoneNumberMutation();
  const { showToast } = useToast();

  const [internalPhoneNumber, setInternalPhoneNumber] = useState<string | null>(
    number || null
  );
  const [internalFetchAttempted, setInternalFetchAttempted] = useState(
    fetchPhoneNumberAttempted
  );

  useEffect(() => {
    if (!internalFetchAttempted) {
      setInternalPhoneNumber(number || null);
    }
  }, [number, internalFetchAttempted]);

  useEffect(() => {
    if (!internalFetchAttempted) {
      setInternalFetchAttempted(fetchPhoneNumberAttempted);
    }
  }, [fetchPhoneNumberAttempted, internalFetchAttempted]);

  const handleRevealClick = async () => {
    try {
      const result = await revealPhoneNumber({
        variables: {
          input: {
            sourcedCandidateId,
            linkedInProfileUrl
          }
        }
      });

      const phoneNumber =
        result.data?.revealSourcedCandidatePhoneNumber?.phoneNumber;

      setInternalFetchAttempted(true);
      setInternalPhoneNumber(phoneNumber || null);
    } catch (error) {
      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage="Try revealing the number again."
            emailSubject="Revealing sourced phone number failed"
          />
        ),
        type: ToastType.Error
      });
    }
  };

  if (internalPhoneNumber) {
    return (
      <Link href={`tel:${internalPhoneNumber}`}>{internalPhoneNumber}</Link>
    );
  }

  if (!internalFetchAttempted) {
    if (loading) {
      return <CircularProgress size={20} />;
    }

    return (
      <Link component="button" onClick={handleRevealClick}>
        Reveal
      </Link>
    );
  }

  if (internalFetchAttempted && !internalPhoneNumber) {
    return <span>Not found</span>;
  }

  if (internalFetchAttempted && internalPhoneNumber) {
    return (
      <Link href={`tel:${internalPhoneNumber}`}>{internalPhoneNumber}</Link>
    );
  }

  return null;
};
